import { TestParserOptions } from '../options/testParserOptions.js';
import { ClassInstanceField, ObjectField, TestCollection } from '../parser/models/all.js';
import { PreProcessContext } from './models/preProcessContext.js';
import * as FieldProcessors from './pre/all.js';

// ordered field processors
const preProcessorOrder = [
  "processDefaultExport",
  "processModule",
  "processClassDef",
  "processClassInstance",
  "processClassInstFunction",
  "processFunction",
  "processFunctionWithCasesArray",
  "processOnlyField",
  "processObject"
];

/**
 * A function that processes an extracted field to identify and create a test or group.
 * @param test - The field to process.
 * @param index - The index of the field in the current scope.
 * @param context - The pre-processing context.
 * @returns True if the processor handled the field and no further processors should run for it.
 */
export type PreProcessor = (
    test: ObjectField | ClassInstanceField, 
    index: number, 
    context: PreProcessContext
) => boolean;

const preProcessors: PreProcessor[] = preProcessorOrder.map(pk => (FieldProcessors as any)[pk]);

/**
 * Executes the pre-processing phase of the test parsing pipeline.
 * Iterates through all extracted fields and applies a series of processors to identify tests.
 * 
 * @param fields - The extracted module or class fields.
 * @param options - Parser configuration options.
 * @returns A {@link TestCollection} containing the initial set of discovered tests.
 */
export function executePreProcessing(fields: (ObjectField | ClassInstanceField)[], options: TestParserOptions): TestCollection {
  const tests = new TestCollection();

  for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++) {
    const field = fields[fieldIndex];
    const preProcessContext = new PreProcessContext(
      tests,
      options
    );

    for (let index = 0; index < preProcessors.length; index++) {
      const processor = preProcessors[index];
      const testProp = processor(field, fieldIndex, preProcessContext);
      if (testProp === true) {
        break;
      }
    }
  }

  // prevent unprocessed fields from being included
  return new TestCollection(tests.filter(field => field !== undefined));
}
